import DetailTemplate from '@/components/detail-template'
import React, { useEffect, useMemo, useCallback } from 'react'
import { Progress, Divider, Select, Typography, Empty, Button, Tooltip } from 'antd'
import { t } from '@/i18n'
import { displayModel } from './display-model'
import { useTaskDetailHooks, useTaskDetailState } from './hooks'
import { useTaskDetailHandlers } from './handlers'
import { importModalOptions, statusMap } from './constants'
import { useTaskDetailData } from './useTaskDetailData'
import { useTaskDetailTable } from './useTaskDetailTable'
import styled from 'styled-components'
import {
  DeleteModal,
  TagSearchOption,
  ImportModal,
  SvgIcon,
  IconFont,
  RecordModal,
} from '@/components'
import TaskCharts from './task-charts'
import { DetailDrawer } from './detail-drawer'
import { CreateReport } from '@/components/create-report'
import ProgressContent from '@/components/detail-template/widgets/progress-content'
import EmptyImg from '@/assets/imges/Empty/Empty.png'
import { formatTimeInterval } from '@/utils'
import { formatDateTime } from '@/utils/date'

const { Text } = Typography

const Detail: React.FC = () => {
  const { taskInfoExpanded, taskInfo: taskInfoModel } = displayModel

  // 使用自定义hooks
  const apiHooks = useTaskDetailHooks()
  const state = useTaskDetailState()

  const {
    form,
    id,
    taskId,
    getTaskInfo,
    taskInfo,
    getAttackType,
    attackType,
    getAttck,
    attckList,
    getUseCaseList,
    loading,
    updateResourceInterceptStatus,
    updateInterceptStatusLoading,
    getTagTreeOptions,
    tagTreeOptions,
    getOfflineLogInfo,
    importRecord,
    getResourceFilterData,
    resourceFilterData,
    getDeviceProtectionList,
    deviceProtectionList,
    getAttackDefenseRanking,
    attackDefenseRanking,
    getRiskLevelDistribution,
    riskLevelDistribution,
    getResourceLevelsRanking,
    resourceLevelsRanking,
    getTemplateLoseEfficacyRanking,
    templateLoseEfficacyRanking,
    getVerifyDistribution,
    verifyDistribution,
    generateReport,
    createLoading,
    deleteTask,
    getResultPluginDetail,
    detailLoading,
    getAttackOrganizationFilterData,
    attackOrganizationFilterData,
    getTemplateFilterData,
    templateFilterData,
    getCancelValidCaseList,
    cancelValidCaseList,
    getSentFailedCaseList,
    sentFailedCaseList,
    getUnsentCaseList,
    unsentCaseList,
    deleteRecordLoading,
    deleteOfflineLogInfo,
  } = apiHooks

  const {
    executeTimeId,
    setExecuteTimeId,
    logInfo,
    setLogInfo,
    failLogInfo,
    setFailLogInfo,
    filterParams,
    setFilterParams,
    filterFormValue,
    setFilterFormValue,
    useCaseList,
    setUseCaseList,
    useCaseTotal,
    setUseCaseTotal,
    fixPayloadCount,
    setFixPayloadCount,
    selectedRowKeys,
    setSelectedRowKeys,
    random,
    setRandom,
    setCardActive,
    importVisible,
    setImportVisible,
    reportVisible,
    setReportVisible,
    setActive,
    deleteVisible,
    setDeleteVisible,
    detailDrawerVisible,
    setDetailDrawerVisible,
    pluginDetail,
    setPluginDetail,
    fixedModalVisible,
    setFixedModalVisible,
    riskLevelSelectOpen,
    setRiskLevelSelectOpen,
    attackTypeSelectOpen,
    setAttackTypeSelectOpen,
    resourceLevelSelectOpen,
    setResourceLevelSelectOpen,
    interceptStatusSelectOpen,
    setInterceptStatusSelectOpen,
    deviceStatusSelectOpen,
    setDeviceStatusSelectOpen,
    infoActiveTab,
    setInfoActiveTab,
    activeTab,
    setActiveTab,
    setProgressContentList,
    recordVisible,
    setRecordVisible,
    verificationMode,
    setVerificationMode,
  } = state

  // 使用数据处理hook
  const { taskScore, convertTaskInfo, progressInfo, tagTreeData } = useTaskDetailData(
    taskInfo,
    resourceFilterData,
    attckList,
    tagTreeOptions,
  )

  // 定义缺失的函数
  const getEchartsData = (result_id: string | undefined = id) => {
    getAttackDefenseRanking({ resultId: result_id }).catch(() => {})
    getRiskLevelDistribution({ resultId: result_id }).catch(() => {})
    getResourceLevelsRanking({ resultId: result_id }).catch(() => {})
    getTemplateLoseEfficacyRanking({ resultId: result_id }).catch(() => {})
    getVerifyDistribution({ resultId: result_id }).catch(() => {})
    getDeviceProtectionList({ resultId: result_id }).catch(() => {})
  }

  const refreshRequest = (result_id = id) => {
    getTaskInfo({ resultId: result_id, taskId }).catch(() => {})
    getAttackType({ resultId: result_id }).catch(() => {})
    getAttck({ result_id, task_id: taskId }).catch(() => {})
    getTagTreeOptions({ taskResultId: result_id }).catch(() => {})
    getAttackOrganizationFilterData({ resultId: result_id }).catch(() => {})
    getTemplateFilterData({ resultId: result_id }).catch(() => {})
    getOfflineLogInfo({ resultId: result_id }).catch(() => {})

    setRandom(new Date().getTime())
    setFilterParams({ page: 1, limit: 20 })
  }

  // 使用事件处理函数
  const handlers = useTaskDetailHandlers(
    id,
    taskId,
    setExecuteTimeId,
    getEchartsData,
    refreshRequest,
    generateReport,
    deleteTask,
    setFailLogInfo,
    updateResourceInterceptStatus,
    setRandom,
    setSelectedRowKeys,
    getUseCaseList,
    setUseCaseList,
    setUseCaseTotal,
    setFixPayloadCount,
    getResultPluginDetail,
    setPluginDetail,
    setDetailDrawerVisible,
    setCardActive,
    sentFailedCaseList,
    getTaskInfo,
  )

  // 删除日志设备导入记录
  const deleteRecord = (importRecordId: string) => {
    deleteOfflineLogInfo({ resultId: id, id: importRecordId })
      .then(() => {
        refreshRequest()
        getEchartsData()
      })
      .catch(_ => {})
  }

  // 先计算 TagSearchOption 的结果
  const tagSearchOptions = TagSearchOption(tagTreeOptions)

  const cascaderOptions = useMemo(
    () =>
      attckList.map(stage => ({
        value: stage.id,
        label: stage.name,
        children: stage.technology.map(tech => ({
          value: tech.id,
          label: tech.name,
          children: tech.subTechnology
            .filter(subTech => subTech.id !== '0')
            .map(subTech => ({
              value: subTech.id,
              label: subTech.name,
            })),
        })),
      })),
    [attckList],
  )

  const options = useMemo(
    () => [
      {
        label: t('用例名称'),
        value: 'keyword',
        type: 'input' as const,
      },
      {
        label: t('ATT&CK'),
        value: 'attckId',
        type: 'cascader',
        mode: 'multiple',
        options: cascaderOptions,
      },
      {
        label: t('用例标签'),
        value: 'tagIds',
        type: 'treeSelect',
        mode: 'multiple',
        options: tagSearchOptions,
      },
      {
        label: t('模板名称'),
        value: 'templateIdList',
        type: 'select',
        mode: 'multiple',
        options: [
          ...(templateFilterData || []).map(item => ({
            label: item.templateName,
            value: item.templateId,
          })),
        ],
      },
      // {
      //   label: '攻击组织',
      //   value: 'aptIds',
      //   type: 'select',
      //   mode: 'multiple',
      //   options: attackOrganizationFilterData?.map(item => ({
      //     label: item.aptName,
      //     value: item.aptId,
      //   })),
      // },
    ],
    [
      attckList,
      tagSearchOptions,
      resourceFilterData,
      attackOrganizationFilterData,
      templateFilterData,
    ],
  )

  // 使用表格相关hook
  const { columns, rowSelection, processTableFilters, processFormFilterValue, processSortOrder } =
    useTaskDetailTable(
      taskInfo,
      attackType,
      handlers,
      setActive,
      riskLevelSelectOpen,
      setRiskLevelSelectOpen,
      attackTypeSelectOpen,
      setAttackTypeSelectOpen,
      resourceLevelSelectOpen,
      setResourceLevelSelectOpen,
      interceptStatusSelectOpen,
      setInterceptStatusSelectOpen,
      deviceStatusSelectOpen,
      setDeviceStatusSelectOpen,
      selectedRowKeys,
      setSelectedRowKeys,
      useCaseList,
      filterParams,
    )

  // 使用提取的事件处理函数
  const { handleToggleTime, handleBack, createReport, handleDelete, handleExpand, handleFixed } =
    handlers

  // 处理筛选参数，移除标识符
  const processFilterParams = (params: any) => {
    const processedParams = { ...params }
    const keyMapping = {
      tree_resource_level: 'resourceLevel',
      tree_risk_level: 'riskLevel',
      tree_intercept_status: 'status',
      tree_device_ids: 'deviceIds',
    }

    Object.keys(processedParams).forEach(key => {
      if (keyMapping[key]) {
        processedParams[keyMapping[key]] = processedParams[key]
        delete processedParams[key]
      }
    })

    return processedParams
  }

  useEffect(() => {
    console.log('id', id)
    getTaskInfo({ resultId: id, taskId })
      .then(res => {
        setReportVisible(prev => ({ ...prev, name: res.data.taskName }))
      })
      .catch(_ => {})
    getAttackType({ resultId: id }).catch(_ => {})
    getAttck({ result_id: id, task_id: taskId }).catch(_ => {})
    getTagTreeOptions({ taskResultId: id }).catch(_ => {})
    getResourceFilterData({ resultId: id }).catch(_ => {})
    getOfflineLogInfo({ resultId: id }).catch(_ => {})
    getAttackOrganizationFilterData({ resultId: id }).catch(() => {})
    getTemplateFilterData({ resultId: id }).catch(() => {})
    getCancelValidCaseList({ resultId: id }).catch(() => {})
    getSentFailedCaseList({ resultId: id }).catch(() => {})
    getUnsentCaseList({ resultId: id }).catch(() => {})
  }, [])

  useEffect(() => {
    if (taskInfo?.verificationMode?.agentToContainer) {
      setVerificationMode('1')
    } else {
      setVerificationMode('2')
    }
  }, [taskInfo])

  // 下拉筛选
  useEffect(() => {
    if (filterFormValue?.field) {
      console.log('filterFormValue', filterFormValue)
      const selectedOption = options.find(opt => opt.value === filterFormValue.field)
      if (selectedOption) {
        const processedValue =
          filterFormValue?.field === 'tagIds'
            ? filterFormValue?.value === ''
              ? null
              : (filterFormValue?.value as string[])?.map(item =>
                  item.split('$').slice(1).join('$'),
                )
            : filterFormValue?.value
        setFilterParams(prev => {
          // 检查当前字段的值是否真的发生了变化
          if (prev[filterFormValue.field] === processedValue) {
            return prev
          }
          const dropdownKeys = options.map(opt => opt.value)
          const updatedParams = {
            ...prev,
            page: 1,
            [filterFormValue.field]: processedValue,
          }
          // 清除其他下拉筛选条件
          dropdownKeys.forEach(key => {
            if (key !== filterFormValue.field && key in updatedParams) {
              delete updatedParams[key]
            }
          })
          return updatedParams
        })
      }
    }
  }, [filterFormValue])

  useEffect(() => {
    const processedParams = processFilterParams(filterParams)
    getUseCaseList({ resultId: id, taskId: taskId, ...processedParams })
      .then(res => {
        setUseCaseList(res.data.items)
        setUseCaseTotal(res.data.total)
        setFixPayloadCount(res.data.manualCorrectCount)
      })
      .catch(_ => {})
  }, [filterParams])

  useEffect(() => {
    let intervalId
    if (importRecord.importing) {
      intervalId = setInterval(() => {
        getOfflineLogInfo({ resultId: id })
          .then(res => {
            if (!res.data.importing) {
              refreshRequest()
              getEchartsData()
            }
          })
          .catch(_ => {})
      }, 3000)
    }

    return () => clearInterval(intervalId)
  }, [importRecord])

  const fixedOptions = useMemo(
    () =>
      taskInfo?.devices?.length > 0
        ? [
            { label: t('拦截成功'), value: 'InterceptStatusSuccess' },
            { label: t('防护失效'), value: 'InterceptStatusDefenceFail' },
            { label: t('检测成功'), value: 'InterceptStatusDetectSuccess' },
          ]
        : [
            { label: '拦截成功', value: 'InterceptStatusSuccess' },
            { label: '防护失效', value: 'InterceptStatusDefenceFail' },
          ],
    [taskInfo],
  )

  // 处理失败用例数据
  const processFailedCaseList = (list: any[]) =>
    Array.isArray(list)
      ? list.map((item: any) => ({
          execLog: item.abnormalReason || '',
          pluginName: item.pluginName,
          execTime: item.execStartTime || '',
          id: item.id,
        }))
      : []

  // 处理未执行用例数据
  const processUnsentCaseList = (list: any[]) =>
    Array.isArray(list)
      ? list.map((item: any) => ({
          execLog: item.execLog,
          pluginName: item.pluginName,
          execTime: item.execTime,
          id: item.id,
        }))
      : []

  // 处理取消验证用例数据
  const processCancelCaseList = (list: any[]) =>
    Array.isArray(list)
      ? list.map((item: any) => ({
          execLog: item.abnormalReason,
          pluginName: item.pluginName,
          execTime: item.execStartTime,
          id: item.id,
        }))
      : []

  // 使用 useMemo 来稳定 progressContentList 的值
  const progressContentList = useMemo(() => {
    if (activeTab === 'failed') {
      console.log('sentFailedCaseList', sentFailedCaseList)
      return processFailedCaseList(sentFailedCaseList?.list)
    } else if (activeTab === 'unexecuted') {
      return processUnsentCaseList(unsentCaseList?.list)
    } else if (activeTab === 'cancel') {
      return processCancelCaseList(cancelValidCaseList?.list)
    }
    return []
  }, [activeTab, sentFailedCaseList, unsentCaseList, cancelValidCaseList])

  // 稳定的点击处理函数
  const handleFailedTabClick = useCallback(() => {
    setActiveTab('failed')
  }, [setActiveTab])

  const handleUnexecutedTabClick = useCallback(() => {
    setActiveTab('unexecuted')
  }, [setActiveTab])

  useEffect(() => {
    if (executeTimeId) return
    setExecuteTimeId(taskInfo?.executionList?.[0]?.result_id)
  }, [taskInfo])

  // 任务内容渲染
  const taskContent = useMemo(
    () => (
      <TaskContent>
        {taskInfoModel.map((item: any, index: number) => {
          const value =
            item.type && item.type !== 'array' && item.type !== 'object'
              ? convertTaskInfo[item.value as string]
              : item.value
          if (value && typeof value === 'object') {
            // 处理进度信息渲染
            return (
              <div key={index} className="progress-info">
                <span className="label">{t(item.key)}：</span>
                {value.map((v: any, i: number) => {
                  if (v.value === 'progressRate') {
                    return (
                      <Progress
                        key={i}
                        strokeColor={{
                          '0%': '#ECEBF9',
                          '100%': '#5047CC',
                        }}
                        success={{ percent: -1 }}
                        percent={progressInfo[v.value as string]}
                      />
                    )
                  } else {
                    return (
                      <div
                        key={i}
                        className={v.value}
                        style={
                          v.value === 'isFinished'
                            ? {
                                background:
                                  statusMap[progressInfo?.[v.value as number]]?.background,
                                color: statusMap[progressInfo?.[v.value as number]]?.color,
                              }
                            : {}
                        }
                      >
                        {v.value === 'successSum' ? (
                          <Tooltip title={t('执行成功用例数量')}>
                            <span>{progressInfo?.[v.value as string]}</span>
                          </Tooltip>
                        ) : v.value === 'failSum' ? (
                          <Tooltip title={t('执行失败用例数量')}>
                            <span>{progressInfo?.[v.value as string]}</span>
                          </Tooltip>
                        ) : (
                          <span>
                            {v.value === 'isFinished'
                              ? statusMap[progressInfo?.[v.value as number]]?.label
                              : progressInfo?.[v.value as string]}
                          </span>
                        )}
                        {v.value === 'successSum' && (
                          <span style={{ color: '#666', margin: '0 4px' }}>/</span>
                        )}
                      </div>
                    )
                  }
                })}
              </div>
            )
          } else {
            return (
              <div key={index}>
                <span className="label">{t(item.key)}：</span>
                <span>{value}</span>
                <Divider type="vertical" />
              </div>
            )
          }
        })}
      </TaskContent>
    ),
    [taskInfoModel, convertTaskInfo, taskInfo],
  )

  // 渲染执行日期选择器
  const renderExecutionDateSelector = () => {
    if (taskInfo?.taskKind === 'TaskKindDetailImmediately') return null

    return (
      <div style={{ fontSize: 13, fontWeight: 400, display: 'flex', alignItems: 'center' }}>
        <div>{t('执行日期：')} </div>
        <Select
          getPopupContainer={node => node?.parentNode?.parentNode}
          style={{ width: 180, marginRight: 10 }}
          value={executeTimeId}
          onChange={timeId => {
            handleToggleTime(timeId)
          }}
        >
          {taskInfo?.executionList?.map((item, index) => (
            <Select.Option key={index} value={item.result_id}>
              {item.create_time}
            </Select.Option>
          ))}
        </Select>
      </div>
    )
  }

  // 渲染验证模式标签
  const renderVerificationModeTabs = () => (
    <div className="left">
      {taskInfo?.verificationMode?.agentToContainer && (
        <div
          className={`tab-item ${verificationMode === '1' ? 'active' : ''}`}
          onClick={() => setVerificationMode('1')}
        >
          <span>{t('验证模式1')}</span>
        </div>
      )}
      {taskInfo?.verificationMode?.containerToListener && (
        <div
          className={`tab-item ${verificationMode === '2' ? 'active' : ''}`}
          onClick={() => setVerificationMode('2')}
        >
          <span>{t('验证模式2')}</span>
        </div>
      )}
    </div>
  )

  // 渲染信息标签
  const renderInfoTabs = () => (
    <div className="right">
      <div
        className={`tab-item ${infoActiveTab === 'taskTemplate' ? 'active' : ''}`}
        onClick={() => setInfoActiveTab('taskTemplate')}
      >
        <span>{t('任务模板')}</span>
      </div>
      <div
        className={`tab-item ${infoActiveTab === 'securityDevice' ? 'active' : ''}`}
        onClick={() => setInfoActiveTab('securityDevice')}
      >
        <span>{t('安全设备')}</span>
      </div>
    </div>
  )

  // 渲染执行类型项目
  const renderExecutionTypeItem = (item: any, index: number, value: any) => {
    if (
      taskInfo.taskKind === 'TaskKindDetailImmediately' ||
      taskInfo.taskKind === 'TaskKindDetailBooking'
    ) {
      return (
        <div className="left-item" key={index}>
          <span className="label">{t(item.key)}：</span>
          <span style={{ marginRight: 8 }}>
            {taskInfo.taskKind === 'TaskKindDetailImmediately' ? t('立即') : t('预定')}
          </span>
          <span>
            {taskInfo?.task_time_list &&
              taskInfo?.task_time_list.length > 0 &&
              taskInfo?.task_time_list.find(v => v.result_id === id)?.task_time}
          </span>
        </div>
      )
    } else {
      return (
        <div className="left-item" key={index}>
          <span className="label">{t(item.key)}：</span>
          {taskInfo?.taskKind === 'TaskKindDetailImmediately' && (
            <span className="type-text">{t('立即')}</span>
          )}
          {taskInfo?.taskKind === 'TaskKindDetailBooking' && (
            <span className="type-text">{t('预定')}</span>
          )}
          {taskInfo?.taskKind === 'TaskKindDetailCycleDay' && (
            <span className="type-text">{t('每日')}</span>
          )}
          {taskInfo?.taskKind === 'TaskKindDetailCycleWeek' && (
            <span className="type-text">{t('每周')}</span>
          )}
          {taskInfo?.taskKind === 'TaskKindDetailCycleMonth' && (
            <span className="type-text">{t('每月')}</span>
          )}
          {taskInfo?.taskKind !== 'TaskKindDetailImmediately' &&
            taskInfo?.taskKind !== 'TaskKindDetailBooking' && (
              <Select
                className="type-select"
                placeholder={t('点击查看')}
                size="small"
                style={{ width: 180 }}
              >
                {taskInfo?.taskTimeList?.map((v, i) => (
                  <Select.Option key={i} value={v}>
                    {v}
                  </Select.Option>
                ))}
              </Select>
            )}
        </div>
      )
    }
  }

  // 渲染Agent项目
  const renderAgentItem = (item: any, index: number) => (
    <div key={index} className="left-item">
      <span className="label">{t(item.key)}：</span>
      <Tooltip
        title={`${
          verificationMode === '1'
            ? taskInfo?.verificationMode?.agentToContainer?.agentName || '-'
            : taskInfo?.verificationMode?.containerToListener?.agentName || '-'
        } - ${taskInfo?.verificationMode?.agentToContainer?.hostIp || '-'}`}
        getPopupContainer={() => document.body}
      >
        <span>
          {verificationMode === '1'
            ? taskInfo?.verificationMode?.agentToContainer?.agentName || '-'
            : taskInfo?.verificationMode?.containerToListener?.agentName || '-'}
        </span>
      </Tooltip>
    </div>
  )

  // 渲染主机名称项目
  const renderHostNameItem = (item: any, index: number) => (
    <div key={index} className="left-item">
      <span className="label">{t(item.key)}：</span>
      <Tooltip
        title={`${
          verificationMode === '1'
            ? taskInfo?.verificationMode?.agentToContainer?.hostName || '-'
            : taskInfo?.verificationMode?.containerToListener?.hostName || '-'
        }`}
        getPopupContainer={() => document.body}
      >
        <span>
          {verificationMode === '1'
            ? taskInfo?.verificationMode?.agentToContainer?.hostName || '-'
            : taskInfo?.verificationMode?.containerToListener?.hostName || '-'}
        </span>
      </Tooltip>
    </div>
  )

  // 渲染安全域项目
  const renderSecurityDomainItem = (item: any, index: number) => (
    <div key={index} className="left-item">
      <span className="label">{t(item.key)}：</span>
      <span>
        {`${
          verificationMode === '1'
            ? taskInfo?.verificationMode?.agentToContainer?.areaName || t('默认安全域')
            : taskInfo?.verificationMode?.containerToListener?.areaName || t('默认安全域')
        }`}
      </span>
    </div>
  )

  // 渲染默认项目
  const renderDefaultItem = (item: any, index: number, value: any) => (
    <div key={index} className="left-item">
      <span className="label">{t(item.key)}：</span>
      <span>
        {item.key === '模式名称'
          ? verificationMode === '1'
            ? t('Agent → 容器')
            : t('容器Agent → Listener')
          : value || '-'}
      </span>
    </div>
  )

  // 任务内容展开渲染
  const taskContentExpanded = useMemo(
    () => (
      <TaskContentExpanded>
        <div className="task-info">
          <div className="sub-title">
            <div>
              <SvgIcon width={16} height={16} name={'data-arrival'} fill={'#fff'} />
              <span className="text">{t('任务信息')}</span>
            </div>
            {renderExecutionDateSelector()}
          </div>
          <div className="tab-box">
            {renderVerificationModeTabs()}
            {renderInfoTabs()}
          </div>
          <div className="content">
            <div className="left">
              {taskInfoExpanded.slice(0, taskInfoExpanded.length - 2).map((item, index) => {
                const value =
                  item.type && item.type !== 'array' && item.type !== 'object'
                    ? taskInfo[item.value as string]
                    : item.value
                if (value && typeof value === 'object') {
                  return renderExecutionTypeItem(item, index, value)
                } else if (item.key === 'Agent') {
                  return renderAgentItem(item, index)
                } else if (item.key === '容器主机名称') {
                  return renderHostNameItem(item, index)
                } else if (item.key === '安全域') {
                  return renderSecurityDomainItem(item, index)
                } else {
                  return renderDefaultItem(item, index, value)
                }
              })}
            </div>
            <div className="right">
              {infoActiveTab === 'taskTemplate' &&
                taskInfoExpanded
                  .slice(taskInfoExpanded.length - 2, taskInfoExpanded.length - 1)
                  .map((item, index) => (
                    <div className="tab-content">
                      {taskInfo[item.value as string]?.length > 0 ? (
                        taskInfo[item.value as string].map((v, i) => (
                          <Text key={i} className="template" ellipsis={{ tooltip: true }}>
                            {v}
                          </Text>
                        ))
                      ) : (
                        <Empty image={EmptyImg} />
                      )}
                    </div>
                  ))}
              {infoActiveTab === 'securityDevice' &&
                taskInfoExpanded
                  .slice(taskInfoExpanded.length - 1, taskInfoExpanded.length)
                  .map((item, index) => (
                    <div className="tab-content">
                      {taskInfo[item.value as string]?.length > 0 ? (
                        taskInfo[item.value as string].map((v, i) => (
                          <Text key={i} className="template" ellipsis={{ tooltip: true }}>
                            {typeof v === 'object'
                              ? `${v.deviceType}-${v.deviceName}-${v.deviceIp}`
                              : v}
                          </Text>
                        ))
                      ) : (
                        <Empty image={EmptyImg} />
                      )}
                    </div>
                  ))}
            </div>
          </div>
        </div>
        {/* 进度信息 */}
        <div className="progress-info">
          {/* payload 列表 */}
          <div style={{ display: !logInfo.visible && !failLogInfo.visible ? 'block' : 'none' }}>
            <div className="sub-title">
              <div className="left">
                <SvgIcon
                  width={16}
                  height={16}
                  name={'task-detail-schedule'}
                  fill={'#fff'}
                  style={{ marginTop: '5px' }}
                />
                <span className="text" style={{ width: 65 }}>
                  {t('执行信息')}
                </span>
              </div>
              <div className="create-time">
                <span>
                  {t('执行时间：')}
                  <span>{progressInfo?.startTime || progressInfo?.createTime}</span>
                  <span style={{ margin: '0 4px' }}>{t('至')}</span>
                  <span>{progressInfo?.endTime || '-'}</span>
                </span>
                <span style={{ marginLeft: 10 }}>
                  {t('时长：')}
                  <span style={{ marginLeft: 5 }}>
                    {t('共')}
                    {formatTimeInterval(
                      progressInfo?.startTime || progressInfo?.createTime,
                      progressInfo?.endTime,
                    )}
                  </span>
                </span>
              </div>
            </div>
            <div
              style={{ display: 'flex', alignItems: 'center', margin: '15px 0', padding: '0 12px' }}
            >
              <div
                className="status"
                style={{
                  background: statusMap[progressInfo.isFinished]?.background,
                  color: statusMap[progressInfo.isFinished]?.color,
                }}
              >
                <span>{statusMap[progressInfo.isFinished]?.label}</span>
              </div>
              <Progress
                className="progress"
                strokeColor={{
                  '0%': '#ECEBF9',
                  '100%': '#5047CC',
                }}
                success={{ percent: -1 }}
                percent={progressInfo?.progressRate}
              />
            </div>
            <div className="execute-box">
              <div className="execute-tab-box">
                <div className="execute-item-title">
                  <div
                    className={
                      activeTab === 'failed'
                        ? 'execute-item-title-text active'
                        : 'execute-item-title-text'
                    }
                    onClick={() => {
                      setActiveTab('failed')
                    }}
                  >
                    {t('执行失败')}（{sentFailedCaseList?.total || 0}）
                  </div>
                  <div
                    className={
                      activeTab === 'unexecuted'
                        ? 'execute-item-title-text active'
                        : 'execute-item-title-text'
                    }
                    onClick={() => {
                      setActiveTab('unexecuted')
                    }}
                  >
                    {t('未执行')}（{unsentCaseList?.total || 0}）
                  </div>
                  <div
                    className={
                      activeTab === 'cancel'
                        ? 'execute-item-title-text active'
                        : 'execute-item-title-text'
                    }
                    onClick={() => {
                      setActiveTab('cancel')
                    }}
                  >
                    {t('取消验证')}（{cancelValidCaseList?.total || 0}）
                  </div>
                </div>
                <div className="execute-success-box">
                  <span>{t('发送成功：')}</span>
                  <span className="success-sum">{progressInfo?.successSum}</span>
                  <span>{t('个')}</span>
                </div>
              </div>
              <div className="execute-tab-content">
                <ProgressContent
                  setLogInfo={setLogInfo}
                  type={activeTab === 'unexecuted' ? 2 : 1}
                  contentList={progressContentList}
                />
              </div>
            </div>
          </div>
          {/* payload 日志 */}
          <div style={{ display: logInfo.visible ? 'block' : 'none' }}>
            <div className="sub-title">
              <div className="left">
                <Button
                  className="return-btn"
                  icon={<IconFont type="icon-fanhui2" className="return-icon" />}
                  onClick={() =>
                    setLogInfo(origin => ({ ...origin, visible: false, content: '', id: '' }))
                  }
                />
                <span className="text">{t('执行日志：')}</span>
              </div>
            </div>
            <div className="log-content">
              <div className="log-panel">
                <div className="content">
                  {logInfo.content && <div className="log-content">{logInfo.content}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </TaskContentExpanded>
    ),
    [
      taskInfo,
      activeTab,
      verificationMode,
      infoActiveTab,
      executeTimeId,
      handleToggleTime,
      taskInfoExpanded,
      id,
      handleFailedTabClick,
      handleUnexecutedTabClick,
      sentFailedCaseList,
      unsentCaseList,
      progressInfo,
      logInfo,
      setLogInfo,
    ],
  )

  const onTableChange = (pagination: any, filters: any, sorter: any) => {
    console.log('sorter', sorter)
    setFilterParams(prev => {
      let newFilters = processTableFilters(filters, prev)
      if (sorter.order === 'ascend') {
        newFilters.sortOrder = 'SORT_ORDER_ASCENDING'
        newFilters.sortField = sorter.field
      } else if (sorter.order === 'descend') {
        newFilters.sortOrder = 'SORT_ORDER_DESCENDING'
        newFilters.sortField = sorter.field
      } else {
        newFilters.sortOrder = null
        newFilters.sortField = null
      }
      Object.keys(newFilters).forEach(key => {
        if (!newFilters[key]) {
          delete newFilters[key]
        }
      })
      return {
        ...newFilters,
        page: pagination.current,
        limit: pagination.pageSize,
      }
    })
  }

  return (
    <DetailContainer>
      <DetailTemplate
        scene={displayModel.name}
        taskName={taskInfo.taskName}
        taskStatus={taskInfo.isFinished}
        isImporting={importRecord.importing}
        canImport={importRecord.canImport}
        taskContent={taskContent}
        taskContentExpanded={taskContentExpanded}
        onTableChange={onTableChange}
        taskCharts={
          <TaskCharts
            taskScore={taskInfo?.score}
            verifyResult={verifyDistribution}
            attackProtectionRanking={attackDefenseRanking}
            deviceProtectionRanking={deviceProtectionList}
            templateProtectionFailureRanking={templateLoseEfficacyRanking}
            riskLevelDistribution={riskLevelDistribution}
            caseLevelDistribution={resourceLevelsRanking}
          />
        }
        treeData={tagTreeData}
        handleExpand={handleExpand}
        filterOptions={options as any}
        filterFormValue={filterFormValue}
        setFilterFormValue={setFilterFormValue}
        filterParams={filterParams}
        setFilterParams={setFilterParams}
        columns={columns}
        dataSource={useCaseList}
        total={useCaseTotal}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        rowSelection={rowSelection}
        fixedCount={fixPayloadCount}
        fixedOptions={fixedOptions}
        handleFixed={values => handleFixed(values, selectedRowKeys, form)}
        fixedLoading={updateInterceptStatusLoading}
        getEchartsData={getEchartsData}
        refreshRequest={refreshRequest}
        loading={loading}
        handleBack={handleBack}
        openRecordModal={setRecordVisible}
        openImportModal={setImportVisible}
        openReportModal={params => setReportVisible(prev => ({ ...prev, ...params }))}
        openDeleteModal={setDeleteVisible}
        fixedModalVisible={fixedModalVisible}
        setFixedModalVisible={setFixedModalVisible}
        scroll={{ x: 1200 }}
      />
      <RecordModal
        open={recordVisible}
        loading={deleteRecordLoading}
        dataSource={importRecord.logDeviceRecords}
        onClose={() => setRecordVisible(false)}
        handleDelete={deleteRecord}
      />
      <ImportModal
        open={importVisible}
        type={'CS'}
        resultId={id}
        refreshPage={() => {
          refreshRequest()
          getEchartsData()
          setRandom(Math.random())
        }}
        onClose={() => setImportVisible(false)}
        options={importModalOptions}
        deviceTypeValue={t('容器安全')}
      />
      <CreateReport
        visible={reportVisible.visible}
        createLoading={createLoading}
        templateType={11}
        onConfirm={e => {
          createReport(e, reportVisible.name)
        }}
        onCancel={() => {
          setReportVisible(origin => ({ ...origin, visible: false }))
        }}
      />

      <DeleteModal
        onOk={handleDelete}
        visible={deleteVisible}
        title={t('删除任务')}
        text={t('此操作将永久删除该任务,是否继续?')}
        onCancel={() => {
          setDeleteVisible(false)
        }}
      />
      <DetailDrawer
        onClose={() => {
          setDetailDrawerVisible(false)
        }}
        dataList={pluginDetail}
        logList={pluginDetail?.device_security}
        visible={detailDrawerVisible}
        loading={detailLoading}
      />
    </DetailContainer>
  )
}

export default Detail

const DetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-bottom: 16px;
  height: 100%;
  .case_name {
    &:hover {
      text-decoration: underline;
    }
  }

  .confidence-level {
    color: #86909c;
    font-size: 12px;

    span {
      margin-right: 12px;
    }
  }
`

const TaskContent = styled.div`
  display: flex;
  align-items: center;

  .label {
    color: #86909c;
    margin-right: 4px;
    flex-shrink: 0;
  }

  .ant-divider-vertical {
    margin: 0 19px;
  }

  .progress-info {
    flex: 1;
    display: flex;
    height: 100%;

    .is_finished,
    .isFinished {
      width: 50px;
      height: 18px;
      border-radius: 9px;
      font-size: 12px;
      text-align: center;
      line-height: 18px;
      flex-shrink: 0;
      margin-right: 10px;
    }

    .ant-progress-line {
      font-size: 12px;
      width: 90%;
      margin-right: 20px;
    }

    .execute_success_count {
      color: #3586ff;
      font-size: 12px;
      flex-shrink: 0;
    }

    .execute_fail_count {
      color: #f5222d;
      font-size: 12px;
      flex-shrink: 0;
    }
  }
`

const TaskContentExpanded = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .task-info {
    flex: 1;
    height: 100%;
    border: 1px solid #e1e3e4;
    margin-right: 16px;
    border-radius: 8px;
    width: 50%;

    .sub-title {
      height: 35px;
      display: flex;
      align-items: center;
      padding-left: 6px;
      justify-content: space-between;

      .text {
        margin-left: 10px;
        font-weight: 500;
        font-size: 16px;
      }
    }
    .tab-box {
      width: 100%;
      height: 32px;
      display: flex;
      align-items: center;
      .left {
        width: 50%;
        min-width: 325px;
        height: 100%;
        display: flex;
        align-items: center;
        border-right: 1px solid #e1e3e4;
      }
      .right {
        width: 50%;
        min-width: 325px;
        height: 100%;
        display: flex;
        align-items: center;
      }
      .tab-item {
        width: 84px;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        &.active {
          color: #4238c9;
          background: #e3e1f5;
        }
        &.disabled {
          color: #333333;
          cursor: not-allowed;
        }
      }
    }

    .content {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .left {
        height: 100%;
        font-size: 12px;
        width: 50%;
        border-right: 1px solid #e1e3e4;
        padding-left: 12px;
        padding-top: 6px;
        padding-right: 12px;

        .left-item {
          margin-bottom: 10px;
          display: flex;
          align-items: center;

          .encode-items-wrapper {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            align-items: center;
          }

          .encode-item {
            display: inline-block;
            padding: 2px 4px;
            background: #f0f0f0;
            border-radius: 4px;
            font-size: 12px;
          }

          .more-dots {
            color: #666;
            margin-left: 2px;
          }

          .label {
            width: 85px;
            flex-shrink: 0;
            color: #86909c;
            font-size: 12px;
          }
          .text {
            font-size: 12px;
          }
        }
      }

      .right {
        width: 50%;
        height: 262px;
        padding-top: 6px;
        .tab-content {
          padding: 0 12px;
          overflow-y: scroll;
          height: 100%;
          .template {
            width: 100%;
            font-size: 13px;
            line-height: 18px;
            margin-bottom: 10px;
          }
        }

        .ant-tabs {
          height: 222px;
          border-radius: 6px;
          border: 1px solid #e1e3e4;

          .ant-tabs-nav {
            padding: 0 12px;
            background: #f1f3f5;
            margin-bottom: 8px;

            .ant-tabs-tab {
              color: #86909c;
            }

            .ant-tabs-tab.ant-tabs-tab-active {
              color: #4238c9;
            }
          }

          .tab-content {
            padding: 0 12px;
            overflow-y: scroll;
            max-height: 170px;
            max-width: 256px;

            .template {
              width: 100%;
              font-size: 12px;
              line-height: 18px;
            }
          }
        }
      }
    }
  }

  .progress-info {
    flex: 1;
    height: 100%;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #e1e3e4;
    width: 50%;

    .status {
      width: 50px;
      height: 18px;
      border-radius: 9px;
      font-size: 12px;
      text-align: center;
      line-height: 18px;
      flex-shrink: 0;
      margin-right: 10px;
    }

    .execute-box {
      min-height: 248px;

      .execute-tab-box {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-right: 12px;
        .execute-item-title {
          height: 32px;
          background: #eeeff1;
          display: flex;
          align-items: center;
          .execute-item-title-text {
            font-size: 14px;
            color: #333;
            height: 100%;
            padding: 0 12px;
            display: flex;
            align-items: center;
            cursor: pointer;
          }
          .active {
            color: #4238c9;
            background: #e3e1f5;
          }
        }
        .execute-success-box {
          .success-sum {
            color: #67c23a;
          }
        }
      }
      .execute-tab-content {
        margin-top: 14px;
      }
    }

    .sub-title {
      height: 35px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #e1e3e4;
      padding: 0 12px;

      .left {
        display: flex;

        .return-btn {
          width: 24px;
          height: 24px;
          display: flex;
          justify-content: center;
          justify-items: center;
          align-items: center;
          margin-right: 12px;
          background: #fff;
          border: 0px;

          &:hover {
            border: 1px solid #4238c9;
            color: #4238c9;
          }
        }

        .return-icon {
          font-size: 12px;
        }

        .text {
          margin-left: 10px;
          font-weight: 500;
          font-size: 16px;
          margin-right: 6px;
          line-height: 24px;
        }

        .status {
          width: 50px;
          height: 18px;
          border-radius: 9px;
          font-size: 12px;
          text-align: center;
          line-height: 18px;
          flex-shrink: 0;
          margin-right: 10px;
        }
      }

      .create-time {
        color: '#666';
      }
    }

    .log-content {
      height: 283px;

      .log-panel {
        border-radius: 8px;
        height: 235px;
        display: flex;
        flex-direction: column;

        .header {
          height: 36px;
          border-radius: 8px 8px 0 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 16px;

          .left {
            height: 100%;
            display: flex;
            padding-top: 10px;

            span:first-child {
              width: 14px;
              height: 14px;
              background: #ff5f56;
              border-radius: 50%;
            }

            span:nth-child(2) {
              width: 14px;
              height: 14px;
              background: #ffbb2f;
              border-radius: 50%;
              margin: 0 8px;
            }

            span:last-child {
              width: 14px;
              height: 14px;
              background: #27c841;
              border-radius: 50%;
            }
          }

          .copy-btn .ant-typography .copy {
            color: #d0d0d0;

            &:hover {
              color: #fff;
            }
          }
        }

        .content {
          flex: 1;
          border-radius: 0 0 8px 8px;
          padding: 14px 12px;
          overflow-y: scroll;
          display: flex;
          flex-direction: column;

          span {
            color: #fff;
            line-height: 20px;
          }
        }
      }
    }

    .information {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 8px;
      color: #666;
      font-size: 12px;

      .success-sum {
        color: #73a0fa;
      }

      .failed-sum {
        color: #f44f4f;

        &:hover {
          cursor: pointer;
          text-decoration: underline;
        }
      }
    }
  }
`
